import React, { useState, useEffect, useMemo } from 'react';
import Pagination from '../../components/Common/Pagination';
import { useNavigate } from 'react-router-dom';
import CSVButton from '../../components/Common/CSVButton';
import { exportToCSV } from '../../utils/csvExport';

interface Warehouse {
  id: number;
  displayUserId: number; // always 1 in the mockup image
  name: string;
  erpId: string;
  zone: string;
  type: string; // Warehouse/ Distributor Type
  subzone: string;
  created: string;
  modified: string;
  approvalStatus: string;
  nonTransactional: string;
  status: 'Active' | 'Inactive';
}

const initialWarehouses: Warehouse[] = [
  {
    id: 1,
    displayUserId: 1,
    name: 'Kanchan Dairy Farm Malad',
    erpId: '6000022',
    zone: 'MUM0484',
    type: '-',
    subzone: 'Malad',
    created: '2017-11-30 11:14:12',
    modified: '2017-11-30 11:14:12',
    approvalStatus: 'Approved',
    nonTransactional: 'No',
    status: 'Active',
  },
  {
    id: 2,
    displayUserId: 1,
    name: 'Kanchan Dairy Farm Malad',
    erpId: '6000022',
    zone: 'MUM0484',
    type: '-',
    subzone: 'Malad',
    created: '2017-11-30 11:14:12',
    modified: '2017-11-30 11:14:12',
    approvalStatus: 'Approved',
    nonTransactional: 'No',
    status: 'Active',
  },
  {
    id: 3,
    displayUserId: 1,
    name: 'Kanchan Dairy Farm Malad',
    erpId: '6000022',
    zone: 'MUM0484',
    type: '-',
    subzone: 'Malad',
    created: '2017-11-30 11:14:12',
    modified: '2017-11-30 11:14:12',
    approvalStatus: 'Approved',
    nonTransactional: 'No',
    status: 'Active',
  },
  {
    id: 4,
    displayUserId: 1,
    name: 'Kanchan Dairy Farm Malad',
    erpId: '6000022',
    zone: 'MUM0484',
    type: '-',
    subzone: 'Malad',
    created: '2017-11-30 11:14:12',
    modified: '2017-11-30 11:14:12',
    approvalStatus: 'Approved',
    nonTransactional: 'No',
    status: 'Active',
  },
  {
    id: 5,
    displayUserId: 1,
    name: 'Kanchan Dairy Farm Malad',
    erpId: '6000022',
    zone: 'MUM0484',
    type: '-',
    subzone: 'Malad',
    created: '2017-11-30 11:14:12',
    modified: '2017-11-30 11:14:12',
    approvalStatus: 'Approved',
    nonTransactional: 'No',
    status: 'Active',
  },
  {
    id: 6,
    displayUserId: 1,
    name: 'Kanchan Dairy Farm Malad',
    erpId: '6000022',
    zone: 'MUM0484',
    type: '-',
    subzone: 'Malad',
    created: '2017-11-30 11:14:12',
    modified: '2017-11-30 11:14:12',
    approvalStatus: 'Approved',
    nonTransactional: 'No',
    status: 'Active',
  }
];

interface WarehousesProps {
  onNavigate?: (path: string, data?: any) => void;
}

const Warehouses: React.FC<WarehousesProps> = () => {
  const navigate = useNavigate();
  const [warehouses, setWarehouses] = useState<Warehouse[]>(initialWarehouses);

  // Filtering & pagination states
  const [activeTab, setActiveTab] = useState<'Active' | 'Inactive' | 'All'>('All');
  const [selectedZone, setSelectedZone] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [entriesLimit, setEntriesLimit] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  // Dropdown & Modal states
  const [isZoneDropdownOpen, setIsZoneDropdownOpen] = useState(false);

  // Reset page when filters change
  useEffect(() => {
    queueMicrotask(() => setCurrentPage(1));
  }, [activeTab, selectedZone, searchQuery, entriesLimit]);

  // Handle outside clicks to close zone dropdown
  useEffect(() => {
    if (!isZoneDropdownOpen) return;
    const handleOutsideClick = () => {
      setIsZoneDropdownOpen(false);
    };
    window.addEventListener('click', handleOutsideClick);
    return () => window.removeEventListener('click', handleOutsideClick);
  }, [isZoneDropdownOpen]);

  // Filtered warehouses
  const filteredWarehouses = useMemo(() => {
    return warehouses.filter((wh: Warehouse) => {
      // Tab Status filter
      if (activeTab !== 'All' && wh.status !== activeTab) {
        return false;
      }

      // Zone filter
      if (selectedZone !== 'All' && wh.zone !== selectedZone) {
        return false;
      }

      // Search text filter
      if (searchQuery.trim() !== '') {
        const query = searchQuery.toLowerCase();
        return (
          wh.name.toLowerCase().includes(query) ||
          wh.erpId.toLowerCase().includes(query) ||
          wh.zone.toLowerCase().includes(query) ||
          wh.subzone.toLowerCase().includes(query) ||
          wh.approvalStatus.toLowerCase().includes(query)
        );
      }

      return true;
    });
  }, [warehouses, activeTab, selectedZone, searchQuery]);

  // Paginated warehouses
  const paginatedWarehouses = useMemo(() => {
    const startIndex = (currentPage - 1) * entriesLimit;
    return filteredWarehouses.slice(startIndex, startIndex + entriesLimit);
  }, [filteredWarehouses, currentPage, entriesLimit]);

  // Actions handlers
  const handleToggleActivation = (id: number) => {
    setWarehouses(prev =>
      prev.map(wh =>
        wh.id === id
          ? { ...wh, status: wh.status === 'Active' ? 'Inactive' : 'Active' }
          : wh
      )
    );
  };

  const handleOpenEditModal = (wh: Warehouse) => {
    navigate('/dashboard/edit-warehouse', { state: { warehouse: wh } });
  };

  // const handleBack = () => {
  //   if (onBack) { onBack(); } else { navigate(-1); }
  // };


  const handleExportCSV = () => {
    const headers = ['ID', 'Warehouse Name', 'ERP ID', 'Zone', 'Subzone', 'Type', 'Approval Status', 'Non Transactional', 'Status', 'Created', 'Modified'];
    const rows = filteredWarehouses.map((w: Warehouse) => [
      w.id,
      w.name,
      w.erpId,
      w.zone,
      w.subzone,
      w.type,
      w.approvalStatus,
      w.nonTransactional,
      w.status,
      w.created,
      w.modified,
    ]);
    exportToCSV(`warehouses_${activeTab.toLowerCase()}`, headers, rows);
  };

  return (
    <div className="space-y-6">

      {/* Dashboard Sub-Header / Control Row */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">

        {/* Title and Back */}
        <div className="flex items-center gap-3">
          {/* <button
            onClick={handleBack}
            className="w-7 h-7 rounded-full go_erp-bg transition-all flex items-center justify-center text-white cursor-pointer"
            title="Go Back"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button> */}
          <h3 className="text-xl font-bold go_erp-text tracking-wide m-0">
            Warehouses
          </h3>
        </div>

        {/* Middle Tabs */}
        <div className="relative flex bg-white p-1 rounded-full w-fit">
          {/* Sliding Background */}
          <div 
            className="absolute top-1 bottom-1 w-24 rounded-full go_erp-bg transition-transform duration-300 ease-out"
            style={{
              transform: `translateX(${
                activeTab === 'All' ? '0' : 
                activeTab === 'Active' ? '100%' : '200%'
              })`
            }}
          />
          {(['All', 'Active', 'Inactive'] as const).map((tab) => {
            const isActive = activeTab === tab;
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`relative z-10 w-24 py-1.5 rounded-full text-xs font-semibold transition-colors duration-300 cursor-pointer ${isActive
                    ? 'text-white'
                    : 'text-slate-500'
                  }`}
              >
                {tab}
              </button>
            );
          })}
        </div>

        {/* Right Dropdown and CSV button */}
        <div className="flex items-center gap-3">
          <div className="relative" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setIsZoneDropdownOpen(!isZoneDropdownOpen)}
              className="flex items-center justify-between px-4 py-2 bg-white rounded-full text-xs font-semibold go_erp-text cursor-pointer transition-colors select-none min-w-[200px]"
            >
              <span>{selectedZone === 'All' ? 'All Zone Selected (4)' : `${selectedZone} Selected`}</span>
              <svg className="w-2.5 h-2.5 go_erp-text fill-current ml-3" viewBox="0 0 24 24">
                <path d="M12 21l-12-18h24z" />
              </svg>
            </button>
            {isZoneDropdownOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white border border-slate-200 rounded-xl shadow-lg py-1.5 z-50 animate-fadeIn">
                {(['All', 'MUM0484', 'MUM0485', 'PUN0129', 'NSK0921'] as const).map((zone) => (
                  <button
                    key={zone}
                    onClick={() => {
                      setSelectedZone(zone);
                      setIsZoneDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2 text-xs font-semibold hover:bg-slate-50 cursor-pointer transition-colors ${(zone === 'All' && selectedZone === 'All') || selectedZone === zone
                        ? 'go_erp-text bg-[#FFF8E1]/40'
                        : 'text-slate-707'
                      }`}
                  >
                    {zone}
                  </button>
                ))}
              </div>
            )}
          </div>
          <CSVButton onClick={handleExportCSV} />
        </div>

      </div>

      {/* Main Table Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-[#ECECF2] overflow-hidden">

        {/* Table Toolbar */}
        <div className="px-6 py-4 border-b border-[#ECECF2] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-[#F9F9FB]/50">

          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-black">Show</span>
            <select
              value={entriesLimit}
              onChange={(e) => setEntriesLimit(Number(e.target.value))}
              className="bg-white border border-gray-300 text-xs font-semibold text-black rounded-xl px-2 py-1 outline-none cursor-pointer transition-colors"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>l
            </select>
            <span className="text-sm font-semibold text-black">entries</span>
          </div>

          <div className="flex flex-wrap items-center gap-4">

            {/* Embedded table search */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-white border border-slate-200 text-xs rounded-full py-1.5 px-4 outline-none focus:border-slate-400 w-48 transition-all"
              />
            </div>

          </div>

        </div>

        {/* Table Wrapper */}
        <div className="overflow-x-auto">
          {/* Header Row */}
          <div className="grid grid-cols-[80px_180px_100px_100px_185px_100px_110px_110px_110px_130px_90px_100px] border-b border-[#ECECF2] px-6 py-4 bg-white min-w-[1395px] items-center">
            <div className="text-xs font-semibold text-[#1F1F1F]">User ID</div>
            <div className="text-xs font-semibold text-[#1F1F1F]">Name</div>
            <div className="text-xs font-semibold text-[#1F1F1F]">ERP/SAP ID</div>
            <div className="text-xs font-semibold text-[#1F1F1F]">Zone</div>
            <div className="text-xs font-semibold text-[#1F1F1F]">Warehouse/ Distributor Type</div>
            <div className="text-xs font-semibold text-[#1F1F1F]">Subzone</div>
            <div className="text-xs font-semibold text-[#1F1F1F]">Created</div>
            <div className="text-xs font-semibold text-[#1F1F1F]">Modified</div>
            <div className="text-xs font-semibold text-[#1F1F1F]">Approval Status</div>
            <div className="text-xs font-semibold text-[#1F1F1F]">Non Transactional</div>
            <div className="text-xs font-semibold text-[#1F1F1F]">Actions</div>
            <div className="text-xs font-semibold text-[#1F1F1F]">Activation</div>
          </div>

          {/* Body Rows */}
          <div className="divide-y divide-[#ECECF2]">
            {paginatedWarehouses.length > 0 ? (
              paginatedWarehouses.map((wh: Warehouse) => (
                <div
                  key={wh.id}
                  className="grid grid-cols-[80px_180px_100px_100px_185px_100px_110px_110px_110px_130px_90px_100px] px-6 py-4 hover:bg-slate-50/40 transition-colors items-center min-w-[1395px]"
                >
                  {/* User ID */}
                  <div className="text-xs text-[#828282]">
                    {wh.displayUserId}
                  </div>

                  {/* Name */}
                  <div className="text-xs text-[#828282]">
                    {wh.name.replace('Dairy Farm', 'Dairy\nFarm')}
                  </div>

                  {/* ERP ID */}
                  <div className="text-xs text-[#828282]">
                    {wh.erpId}
                  </div>

                  {/* Zone */}
                  <div className="text-xs text-[#828282]">
                    {wh.zone}
                  </div>

                  {/* Warehouse/Distributor Type */}
                  <div className="text-xs text-[#828282]">
                    {wh.type}
                  </div>

                  {/* Subzone */}
                  <div className="text-xs text-[#828282]">
                    {wh.subzone}
                  </div>

                  {/* Created */}
                  <div className="text-xs text-[#828282]">
                    {wh.created.replace(' ', '\n')}
                  </div>

                  {/* Modified */}
                  <div className="text-xs text-[#828282]">
                    {wh.modified.replace(' ', '\n')}
                  </div>

                  {/* Approval Status */}
                  <div className="text-xs text-[#828282]">
                    {wh.approvalStatus}
                  </div>

                  {/* Non Transactional */}
                  <div className="text-xs text-[#828282]">
                    {wh.nonTransactional}
                  </div>

                  {/* Actions */}
                  <div className="text-xs">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleOpenEditModal(wh)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#F9C327] hover:bg-[#e2af20] text-slate-900 font-bold text-xs rounded-xl shadow-2xs transition-all cursor-pointer"
                        title="View Warehouse"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                        View
                      </button>
                      <button
                        onClick={() => handleOpenEditModal(wh)}
                        className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
                        title="Edit Warehouse"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                      </button>
                    </div>
                  </div>

                  {/* Activation */}
                  <div className="text-xs">
                    <button
                      onClick={() => handleToggleActivation(wh.id)}
                      className={`text-white text-sm font-medium px-4 py-1 rounded-xl cursor-pointer transition-all tracking-wide ${wh.status === 'Active'
                          ? 'go_erp-bg'
                          : 'bg-emerald-600 hover:bg-emerald-700'
                        }`}
                    >
                      {wh.status === 'Active' ? 'Deactivate' : 'Activate'}
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="px-6 py-10 text-center text-xs font-semibold text-slate-400">
                No warehouses found matching filters.
              </div>
            )}
          </div>
        </div>

        {/* Table Footer / Pagination */}
        <Pagination
          currentPage={currentPage}
          totalEntries={filteredWarehouses.length}
          entriesLimit={entriesLimit}
          onPageChange={setCurrentPage}
        />

      </div>



    </div>
  );
};

export default Warehouses;